import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {BaseComponent} from "../../../../BaseComponent";
import {DeviceTypeLite} from "../../../../../models/device-type/DeviceTypeLite";
import {
  CompareActionEnum,
  CompareGroupVM,
  CompareMetricVM,
  MetricSyncStatusEnum
} from "../../../../../models/device/DeviceTemplateCompare";
import {ProtocolTypeEnum} from "../../../../../enums/registry/ProtocolTypeEnum";
import {getRowKey} from "../../../../../utils/rowKey";

// This component computes the diff/match/action state and emits intent events; the
// parent (GatewayDeviceComponent) accumulates them into a local draft and only
// calls saveDevicePendingConfig when the Save button fires the `save` output
// (Cancel discards the draft). It's also responsible for refreshing
// `templateGroups` when selectedDeviceTypeId changes (this component does not
// fetch data itself).
@Component({
  selector: 'app-device-template-compare',
  templateUrl: './device-template-compare.component.html',
  styleUrls: ['./device-template-compare.component.css']
})
export class DeviceTemplateCompareComponent extends BaseComponent implements OnChanges {

  @Input() deviceId!: number;
  @Input() deviceTypes: DeviceTypeLite[] = [];
  @Input() deviceProtocol!: ProtocolTypeEnum;
  @Input() defaultDeviceTypeId?: number;

  // The device's current reference-model override (see DeviceDraft.defaultType). Drives the
  // type select's initial value (falls back to defaultDeviceTypeId/typeId when null) and
  // whether "Set as reference model" starts on.
  @Input() defaultType?: number;

  // Left side (template) tree for the currently selected device type. Parent
  // must swap this whenever selectedDeviceTypeId changes.
  @Input() templateGroups: CompareGroupVM[] = [];

  // Right side (device) tree: the device's own group/metric instances, with
  // originId already populated from the pending-config draft's metricGroupId/metricId.
  @Input() deviceGroups: CompareGroupVM[] = [];

  @Output() visibleChange = new EventEmitter<boolean>();

  get visible(): boolean {
    return this._visible;
  }

  @Input() set visible(value: boolean) {
    this._visible = value;
    if (value)
      this.initSelection();
    this.visibleChange.emit(value);
  }

  @Output() selectedDeviceTypeIdChange = new EventEmitter<number>();

  get selectedDeviceTypeId(): number | undefined {
    return this._selectedDeviceTypeId;
  }

  @Input() set selectedDeviceTypeId(value: number | undefined) {
    this._selectedDeviceTypeId = value;
    // "Set as reference model" tracks whether the current selection IS the device's
    // existing default type - flips off the moment you pick something else, back on if you
    // land back on it. The user can still override it manually via the switch afterward.
    this.useAsReferenceType = this.referenceType !== undefined && value === this.referenceType;
    this.selectedDeviceTypeIdChange.emit(value);
  }

  @Output() addGroup = new EventEmitter<CompareGroupVM>();
  @Output() addMetric = new EventEmitter<{ group: CompareGroupVM, metric: CompareMetricVM }>();
  @Output() replaceMetric = new EventEmitter<{ group: CompareGroupVM, metric: CompareMetricVM }>();
  @Output() saveCompare = new EventEmitter<{useAsReferenceType: boolean, selectedDeviceTypeId?: number}>();
  @Output() cancelCompare = new EventEmitter<void>();
  @Output() resetFromModel = new EventEmitter<number>();
  @Output() deleteGroup = new EventEmitter<CompareGroupVM>();
  @Output() deleteMetric = new EventEmitter<{ group: CompareGroupVM, metric: CompareMetricVM }>();

  protected readonly CompareActionEnum = CompareActionEnum;

  private _visible: boolean = false;
  private _selectedDeviceTypeId?: number;

  selectedTemplateGroupId?: number;
  selectedDeviceGroup?: CompareGroupVM;
  useAsReferenceType: boolean = false;
  referenceType?: number;

  templateGroupRows: CompareGroupVM[] = [];
  templateMetricRows: CompareMetricVM[] = [];
  filteredDeviceTypes: DeviceTypeLite[] = [];

  get selectedTemplateGroup(): CompareGroupVM | undefined {
    return this.templateGroupRows.find(g => g.id === this.selectedTemplateGroupId);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['templateGroups'] && !changes['templateGroups'].firstChange)
      this.selectedTemplateGroupId = undefined;
    if (changes['deviceGroups'] && this.selectedDeviceGroup)
      this.selectedDeviceGroup = this.deviceGroups.find(g => getRowKey(g) === getRowKey(this.selectedDeviceGroup));
    if (changes['templateGroups'] || changes['deviceGroups'])
      this.rebuildRows();
    if (changes['deviceProtocol'] || changes['deviceTypes'])
      this.filterDeviceTypes();
  }

  private initSelection() {
    this.selectedDeviceTypeId = this.defaultType ?? this.defaultDeviceTypeId;
    this.referenceType = this.selectedDeviceTypeId;
    this.selectedTemplateGroupId = undefined;
    this.selectedDeviceGroup = undefined;
    this.rebuildRows();
  }

  onTemplateGroupClick(group: CompareGroupVM) {
    this.selectedTemplateGroupId = group.id;
    this.selectedDeviceGroup = this.deviceGroups.find(dg => dg.originId != null && dg.originId === group.id);
    this.rebuildMetricRows();
  }

  onDeviceGroupClick(group: CompareGroupVM) {
    this.selectedDeviceGroup = group;
    this.rebuildMetricRows();
  }

  onDeleteDeviceGroup(group: CompareGroupVM) {
    if (this.selectedDeviceGroup && getRowKey(this.selectedDeviceGroup) === getRowKey(group))
      this.selectedDeviceGroup = undefined;
    this.deleteGroup.emit(group);
  }

  onDeleteDeviceMetric(metric: CompareMetricVM) {
    if (!this.selectedDeviceGroup)
      return;
    this.deleteMetric.emit({group: this.selectedDeviceGroup, metric});
  }

  onUseAsReferenceToggle(checked: boolean) {
    this.useAsReferenceType = checked;
    if (checked)
      this.referenceType = this.selectedDeviceTypeId;
  }

  onAddGroup(group: CompareGroupVM) {
    if (group.action !== CompareActionEnum.ADD)
      return;
    this.addGroup.emit(group);
  }

  onAddAllGroups(groups: CompareGroupVM[]) {
    groups.forEach(group => this.onAddGroup(group));
  }

  onAddMetric(metric: CompareMetricVM) {
    if (!this.selectedDeviceGroup || metric.action !== CompareActionEnum.ADD)
      return;
    this.addMetric.emit({group: this.selectedDeviceGroup, metric});
  }

  onAddAllMetrics(metrics: CompareMetricVM[]) {
    metrics.forEach(metric => this.onAddMetric(metric));
  }

  onReplaceMetric(metric: CompareMetricVM) {
    if (!this.selectedDeviceGroup || metric.action !== CompareActionEnum.REPLACE)
      return;
    this.replaceMetric.emit({group: this.selectedDeviceGroup, metric});
  }

  onSaveClick() {
    this.saveCompare.emit({useAsReferenceType: this.useAsReferenceType, selectedDeviceTypeId: this.selectedDeviceTypeId});
  }

  onCancelClick() {
    this.visible = false;
    this.cancelCompare.emit();
  }

  private rebuildRows() {
    this.templateGroupRows = this.templateGroups.map(group => ({
      ...group,
      matchedDeviceGroup: this.deviceGroups.find(dg => dg.originId != null && dg.originId === group.id),
      action: this.deviceGroups.some(dg => dg.originId != null && dg.originId === group.id)
        ? CompareActionEnum.DISABLED
        : CompareActionEnum.ADD
    }));
    this.rebuildMetricRows();
  }

  private rebuildMetricRows() {
    const templateGroup = this.templateGroupRows.find(g => g.id === this.selectedTemplateGroupId);
    if (!templateGroup) {
      this.templateMetricRows = [];
      return;
    }
    this.templateMetricRows = templateGroup.metrics.map(metric => this.buildMetricRow(metric));
  }

  private buildMetricRow(metric: CompareMetricVM): CompareMetricVM {
    const matchedDeviceMetric = this.selectedDeviceGroup?.metrics.find(
      dm => dm.originId != null && dm.originId === metric.id);

    if (!matchedDeviceMetric) {
      return {
        ...metric,
        matchedDeviceMetric: undefined,
        syncStatus: MetricSyncStatusEnum.NONE,
        action: this.selectedDeviceGroup ? CompareActionEnum.ADD : CompareActionEnum.DISABLED
      };
    }

    const identical = this.isMetricConfigIdentical(metric, matchedDeviceMetric);
    return {
      ...metric,
      matchedDeviceMetric,
      syncStatus: identical ? MetricSyncStatusEnum.SYNCED : MetricSyncStatusEnum.CONFLICT,
      action: identical ? CompareActionEnum.DISABLED : CompareActionEnum.REPLACE
    };
  }

  private isMetricConfigIdentical(template: CompareMetricVM, device: CompareMetricVM): boolean {
    return template.name === device.name
      && (template.dataUnitId ?? null) === (device.dataUnitId ?? null)
      && (template.divider ?? null) === (device.divider ?? null)
      && (template.decimalDigits ?? null) === (device.decimalDigits ?? null)
      && JSON.stringify(template.transportConfiguration ?? null) === JSON.stringify(device.transportConfiguration ?? null);
  }

  private filterDeviceTypes() {
    this.filteredDeviceTypes = this.deviceTypes.filter(dt => dt.protocolType === this.deviceProtocol);
  }

}
